import { useState, useRef, useEffect } from 'react';
import { Bell, Check, CheckCheck, X, Info, AlertCircle, CheckCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { useAppStore } from '../../store/appStore';
import { Notification } from '../../store/appStore';

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

function NotificationIcon({ type }: { type: Notification['type'] }) {
  switch (type) {
    case 'success':
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    case 'error':
      return <AlertCircle className="w-5 h-5 text-red-500" />;
    case 'warning':
      return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    default:
      return <Info className="w-5 h-5 text-blue-500" />;
  }
}

export default function NotificationCenter() {
  const {
    notifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    clearNotifications
  } = useAppStore();

  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const filteredNotifications = filter === 'unread'
    ? notifications.filter((n) => !n.read)
    : notifications;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markNotificationAsRead(notification.id);
    }

    if (notification.actionUrl) {
      window.open(notification.actionUrl, '_blank');
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-recipe-card transition-colors"
      >
        <Bell className="w-5 h-5 text-recipe-text" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-recipe-accent text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-recipe-card border border-recipe-border rounded-xl shadow-2xl overflow-hidden z-50 animate-slide-up">
          {/* Header */}
          <div className="p-4 border-b border-recipe-border">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-recipe-text">Notifications</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-recipe-bg rounded"
              >
                <X className="w-4 h-4 text-recipe-text-muted" />
              </button>
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'all'
                    ? 'bg-recipe-accent text-white'
                    : 'text-recipe-text-muted hover:text-recipe-text'
                }`}
              >
                All ({notifications.length})
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'unread'
                    ? 'bg-recipe-accent text-white'
                    : 'text-recipe-text-muted hover:text-recipe-text'
                }`}
              >
                Unread ({unreadCount})
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-recipe-border scrollbar-track-transparent">
            {filteredNotifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-recipe-text-muted mx-auto mb-3" />
                <p className="text-recipe-text-muted">
                  {filter === 'unread' ? 'No unread notifications' : 'No notifications'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-recipe-border">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-recipe-bg transition-colors cursor-pointer group ${
                      !notification.read ? 'bg-recipe-accent/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-0.5">
                        <NotificationIcon type={notification.type} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-recipe-text mb-1">
                              {notification.title}
                            </h4>
                            <p className="text-sm text-recipe-text-muted">
                              {notification.message}
                            </p>
                          </div>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-recipe-accent rounded-full flex-shrink-0 mt-1.5" />
                          )}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-recipe-text-muted">
                            {formatTimeAgo(notification.timestamp)}
                          </span>

                          {notification.actionText && (
                            <span className="flex items-center gap-1 text-xs text-recipe-accent font-medium">
                              {notification.actionText}
                              <ExternalLink className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Mark as Read */}
                      {!notification.read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markNotificationAsRead(notification.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-recipe-accent/10 rounded transition-opacity"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4 text-recipe-text-muted" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-recipe-border bg-recipe-bg/50 flex items-center justify-between gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllNotificationsAsRead}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-recipe-accent hover:bg-recipe-accent/10 rounded-lg transition-colors font-medium"
                >
                  <CheckCheck className="w-4 h-4" />
                  Mark all as read
                </button>
              )}
              <button
                onClick={clearNotifications}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-recipe-text-muted hover:bg-recipe-bg rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
